import { useId, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, toApiError, unwrap } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { ErrorMessage, Loading } from "../../components/ui";
import { t } from "../../i18n/t";
import { formatDate, roleLabel } from "../../lib/format";
import { routes } from "../../lib/routes";
import { usePageTitle } from "../../lib/usePageTitle";
import { useQuery } from "../../lib/useQuery";
import { PasswordFields, passwordProblem } from "./PasswordFields";

export function InvitationPage() {
  const { token = "" } = useParams();
  const { status, refresh } = useAuth();
  const navigate = useNavigate();
  const id = useId();
  const info = useQuery(`invitation:${token}`, (signal) =>
    unwrap(api.GET("/api/auth/invitations/{token}", { params: { path: { token } }, signal })),
  );
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedExisting, setAcceptedExisting] = useState<string | null>(null);
  usePageTitle(t("auth.inviteTitle"));

  async function accept(e: FormEvent) {
    e.preventDefault();
    const existing = info.data?.existing_user ?? false;
    if (!existing) {
      const problem = passwordProblem(password, confirm);
      if (problem) {
        setError(problem);
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      const res = await unwrap(
        api.POST("/api/auth/invitations/{token}/accept", {
          params: { path: { token } },
          body: existing ? { name: "" } : { name: name.trim(), password },
        }),
      );
      if (existing) {
        // Signed-in users see the new org right away; others log in with their own password.
        if (status === "authenticated") {
          await refresh();
          navigate(routes.home(), { replace: true });
        } else {
          setAcceptedExisting(res.detail ?? t("auth.inviteAcceptedExisting"));
        }
      } else {
        // A new account gets a session cookie straight away.
        await refresh();
        navigate(routes.home(), { replace: true });
      }
    } catch (err) {
      setError(toApiError(err).userMessage);
    } finally {
      setBusy(false);
    }
  }

  if (info.loading) return <Loading />;
  if (info.error) {
    return (
      <div className="auth-card card">
        <h1>{t("auth.inviteTitle")}</h1>
        {info.error.status === 404 ? (
          <p className="notice notice-error" role="alert">
            {t("auth.inviteInvalid")}
          </p>
        ) : (
          <ErrorMessage error={info.error} onRetry={info.reload} />
        )}
        <p>
          <Link to={routes.login()}>{t("auth.toLogin")}</Link>
        </p>
      </div>
    );
  }
  const inv = info.data;
  if (!inv) return null;

  return (
    <div className="auth-card card">
      <h1>{t("auth.inviteTitle")}</h1>
      <p>
        {t("auth.inviteIntro", { org: inv.org_name, role: roleLabel(inv.role).toLowerCase() })}{" "}
        {t("auth.inviteEmail", { email: inv.email })}
      </p>
      <p className="muted small">{t("auth.inviteExpires", { date: formatDate(inv.expires_at) })}</p>
      {acceptedExisting ? (
        <>
          <p className="notice notice-success" role="status">
            {acceptedExisting}
          </p>
          <p>
            <Link to={routes.login()} className="btn btn-primary">
              {t("auth.toLogin")}
            </Link>
          </p>
        </>
      ) : (
        <form onSubmit={(e) => void accept(e)} noValidate>
          {inv.existing_user ? (
            <p>{t("auth.inviteExisting")}</p>
          ) : (
            <>
              <p className="muted">{t("auth.inviteNew")}</p>
              <div className="field">
                <label htmlFor={`${id}-name`}>{t("auth.name")}</label>
                <input
                  id={`${id}-name`}
                  autoComplete="name"
                  required
                  maxLength={200}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor={`${id}-email`}>{t("auth.email")}</label>
                <input
                  id={`${id}-email`}
                  type="email"
                  autoComplete="username"
                  value={inv.email}
                  readOnly
                />
              </div>
              <PasswordFields
                password={password}
                confirm={confirm}
                onPassword={setPassword}
                onConfirm={setConfirm}
              />
            </>
          )}
          {error && (
            <p className="notice notice-error" role="alert">
              {error}
            </p>
          )}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
              {inv.existing_user ? t("auth.inviteAccept") : t("auth.createAccount")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
